import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Page } from "@/components/Page";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { RevealStagger, StaggerItem } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { IMAGES, SERVICES } from "@/data/site";

const Services = () => {
  return (
    <Page>
      <SEO title="Services" description="Customs clearing, freight forwarding, warehousing, inland transportation, project cargo, vessel chartering and marine insurance — integrated logistics from Speed Shipping Agencies." />

      <PageHero
        eyebrow="Our Services"
        title="Integrated maritime & logistics services"
        intro="Eight specialist divisions, delivered with operational precision and full accountability — from border clearance to ocean chartering."
        image={IMAGES.cranes}
      />

      <section className="py-24 lg:py-32">
        <div className="ssa-container">
          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="services-grid">
            {SERVICES.map((s, idx) => (
              <StaggerItem key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-grid-card-${s.slug}`}
                  className="group relative flex flex-col h-full rounded-2xl overflow-hidden border border-[color:var(--ssa-border-subtle)] bg-white transition-shadow duration-300 hover:shadow-[0_22px_55px_rgba(8,28,58,0.14)] hover:-translate-y-1 will-change-transform"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img src={s.image} alt={s.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 card-overlay" />
                    {idx === 0 && <span className="absolute top-0 inset-x-0 h-[2px]" style={{ background: "var(--ssa-gold)" }} />}
                    <div className="absolute bottom-3 left-4 flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-turquoise shadow-lg">
                      <s.icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <h3 className="text-lg text-navy">{s.name}</h3>
                    <p className="mt-2 text-sm text-secondary-ssa leading-relaxed flex-1">{s.short}</p>
                    <ul className="mt-4 space-y-1.5">
                      {s.highlights.map((h) => (
                        <li key={h} className="flex items-center gap-2 text-xs text-[color:var(--ssa-text-primary)]">
                          <span className="h-1 w-1 rounded-full bg-turquoise" /> {h}
                        </li>
                      ))}
                    </ul>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-royal">
                      View service
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="ssa-container">
          <div className="rounded-3xl ocean-sheen border border-[color:var(--ssa-border-subtle)] p-10 lg:p-14 text-center">
            <h2 className="text-navy text-2xl sm:text-3xl max-w-2xl mx-auto leading-tight">
              A single, accountable partner across your entire supply chain
            </h2>
            <div className="mt-7 flex justify-center">
              <Button to="/contact" variant="primary" size="lg">
                Speak with our team <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Page>
  );
};

export default Services;
